import { useState, useRef, useEffect } from 'react';
import { Printer, Loader2, Trash2, ArrowLeft, Building, User, FileText, ExternalLink, Calendar, CreditCard, Hash } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import { ChartOfAccount } from '@/types/index'
import PrintableDocument from '@/components/PrintableDocument';
import ReceiptsService from '@/api/receipts.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import FormModal from '@/components/FormModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { format, parseISO } from 'date-fns';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import ChartOfAccountsService from '@/api/chartofaccounts.service';
import TransactionsService from '@/api/transactions.service';

// Update the Customer type to match the schema
type Customer = {
  id: number;
  publicId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  street: string;
  city: string;
  state: string;
  zipCode?: string; // Optional
  country: string;
  companyName?: string; // Optional
};

export default function DocumentForm({ receiptId }: { receiptId: string }) {
  const [logo, setLogo] = useState<string>();
  const [signature, setSignature] = useState<string>();
  const [currency, setCurrency] = useState<Currency>('USD');
  const [amount, setAmount] = useState<number>(0);
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [invoiceId, setInvoiceId] = useState<number>(0);
  const [invoiceAmount, setInvoiceAmount] = useState<number>(0);
  const [invoiceTotal, setInvoiceTotal] = useState<number>(0);
  const [paymentMethodName, setPaymentMethodName] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>('');
  const [userDetails, setUserDetails] = useState<any>();
  const [companyDetails, setCompanyDetails] = useState({
    publicId: '',
    name: '',
    address: '',
    city: '',
    country: '',
    emailAddress: '',
    phoneNumber: '',
    logoImg: ''
  });
  const [customerDetails, setCustomerDetails] = useState<Customer>({
    id: 0,
    publicId: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    companyName: ''
  });
  const [receiptNumber, setReceiptNumber] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentAccount, setPaymentAccount] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const printableRef = useRef<PrintableRef>(null);
  
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [isRecordingTransaction, setIsRecordingTransaction] = useState(false);
  const [transactionRecorded, setTransactionRecorded] = useState(false);

  useEffect(() => {
    const fetchReceipt = async () => {
      setIsLoading(true);
      try {
        const response = await ReceiptsService.getReceiptById(parseInt(receiptId));
        const { data } = response;
        console.log("getReceiptById data", data);
        if (data.result === 'success') {
          const receipt = data.receipt;
          setCompanyDetails(receipt.business);
          setCustomerDetails(receipt.customer);
          setUserDetails(receipt.user);
          setAmount(receipt.amount);
          setInvoiceNumber(receipt.invoiceNumber);
          setInvoiceId(receipt.invoiceId);
          setInvoiceAmount(receipt.invoiceAmount);
          setInvoiceTotal(receipt.invoiceTotal);
          setPaymentMethodName(receipt.paymentMethodName);
          setSignature(receipt.signature);
          setCurrency(receipt.currency);
          setReceiptNumber(receipt.receiptNumber);
          setPaymentDate(receipt.paymentDate);
          setPaymentReference(receipt.paymentReference);
          setPaymentAccount(receipt.paymentAccount);
        } else {
          showToastWithTimeout("error", "❌ Fetch Receipt Failed", data.message, 3000);
        }
      } catch (error) {
        console.error(error);
        showToastWithTimeout("error", "❌ Fetch Receipt Failed", "An error occurred while fetching the receipt.", 3000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReceipt();
  }, [receiptId]);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      setCompanyDetails({
        publicId: selectedBusiness.publicId,
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        country: selectedBusiness.country,
        emailAddress: selectedBusiness.emailAddress,
        phoneNumber: selectedBusiness.phoneNumber,
        logoImg: selectedBusiness.logoImg
      });
      setLogo(selectedBusiness.logoImg);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    const fetchChartOfAccounts = async () => {
      try {
        if (!selectedBusiness) return;
        const response = await ChartOfAccountsService.getAllChartOfAccounts(selectedBusiness.id);
        if (response.data.result === 'success') {
          // Filter to include only income accounts and active accounts
          const filteredAccounts = response.data.chartOfAccounts.filter(
            (account: ChartOfAccount) => account.isActive && 
            (account.type === 'INCOME' || account.type === 'ASSET')
          );
          setChartOfAccounts(filteredAccounts);
        }
      } catch (error) {
        console.error("Error fetching chart of accounts:", error);
      }
    };

    fetchChartOfAccounts();
  }, [selectedBusiness]);

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await ReceiptsService.deleteReceipt(parseInt(receiptId));
      if (response.data.result === 'success') {
        showToastWithTimeout("success", "✅ Success", "Receipt deleted successfully", 3000);
        setIsDeleteModalOpen(false);
        navigate('/receipts');
      } else {
        showToastWithTimeout("error", "❌ Delete Failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Delete Failed", "An error occurred while deleting the receipt", 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const handleRecordTransaction = async () => {
    if (!selectedAccount) {
      showToastWithTimeout("error", "❌ Account Required", "Please select an account to record this transaction.", 3000);
      return;
    }

    try {
      setIsRecordingTransaction(true);
      
      const transactionData = {
        chartOfAccountId: parseInt(selectedAccount),
        transactionType: "CREDIT", // Receipt is always a credit to the selected account
        amount: amount, // Convert to cents for storage
        currency: currency,
        description: `Receipt payment for Invoice #${invoiceNumber}`,
        reference: receiptNumber,
        referenceId: parseInt(receiptId),
        referenceType: "receipt",
        transactionDate: paymentDate,
      };
      
      const response = await TransactionsService.createTransaction(transactionData, selectedBusiness.id);
      
      if (response.data.result === 'success') {
        showToastWithTimeout("success", "✅ Transaction Recorded", "The payment has been recorded in the accounting system.", 3000);
        setTransactionRecorded(true);
      } else {
        showToastWithTimeout("error", "❌ Transaction Failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error("Error recording transaction:", error);
      showToastWithTimeout("error", "❌ Transaction Failed", "An error occurred while recording the transaction.", 3000);
    } finally {
      setIsRecordingTransaction(false);
    }
  };

  console.log("invoiceTotal", invoiceTotal);
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <h1 className="text-2xl font-semibold">Receipt {receiptNumber}</h1>
          {receiptNumber && (
            <Badge variant="outline" className="ml-2 text-sm">
              #{receiptNumber}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrint}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            disabled={!receiptNumber || isLoading}
          >
            <Printer size={16} />
            Print
          </Button>
          <Button
            onClick={() => setIsDeleteModalOpen(true)}
            variant="destructive"
            size="sm"
            className="flex items-center gap-1"
            disabled={isDeleting || isLoading}
          >
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
            Delete
          </Button>
        </div>
      </div>

      <FormModal
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        title="Delete Receipt"
      >
        <div className="space-y-4">
          <p className="text-muted-foreground">Are you sure you want to delete this receipt? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Receipt
            </Button>
          </div>
        </div>
      </FormModal>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : 
        companyDetails && ( 
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Hash size={14} />
                  Receipt Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Receipt Number</p>
                      <p className="font-medium">{receiptNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Invoice Number</p>
                      <p className="font-medium">
                        <a 
                          href={`/invoice/${invoiceId}`}
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          {invoiceNumber}
                          <ExternalLink size={14} />
                        </a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Payment Method</p>
                      <div className="flex items-center gap-2">
                        <CreditCard size={16} className="text-muted-foreground" />
                        <p>{paymentMethodName}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Payment Date</p>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-muted-foreground" />
                        <p>{formatDate(paymentDate)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {paymentReference && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Payment Reference</p>
                      <p className="font-mono text-sm bg-muted px-2 py-1 rounded">{paymentReference}</p>
                    </div>
                  )}
                  {paymentAccount && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Payment Account</p>
                      <p className="font-mono text-sm bg-muted px-2 py-1 rounded">{paymentAccount}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <CreditCard size={14} />
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Invoice Total</p>
                    <p className="text-2xl font-semibold">{formatCurrency(invoiceTotal ? invoiceTotal : invoiceAmount)}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Receipt Amount</p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-semibold text-green-600">{formatCurrency(amount)}</p>
                      {amount < invoiceAmount && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          Partial Payment
                        </Badge>
                      )}
                      {amount >= invoiceAmount && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Paid in Full
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {amount < invoiceAmount && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Remaining Balance</p>
                      <p className="text-lg font-medium text-amber-600">{formatCurrency(invoiceAmount - amount)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex flex-col md:flex-row gap-6">
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Building size={14} />
                  Business Information
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-primary"
                  onClick={() => window.location.href = `/business/${companyDetails.publicId}`}
                >
                  <ExternalLink size={12} className="mr-1" />
                  View Business
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {companyDetails.logoImg && (
                    <div className="mb-4">
                      <img 
                        src={companyDetails.logoImg} 
                        alt={companyDetails.name} 
                        className="h-12 object-contain" 
                      />
                    </div>
                  )}
                  <p className="font-medium">{companyDetails.name}</p>
                  <p className="text-sm text-muted-foreground">{companyDetails.address}</p>
                  <p className="text-sm text-muted-foreground">{companyDetails.city}, {companyDetails.country}</p>
                  <Separator className="my-2" />
                  <div className="text-sm">
                    <p className="text-muted-foreground">{companyDetails.emailAddress}</p>
                    <p className="text-muted-foreground">{companyDetails.phoneNumber}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="flex-1 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <User size={14} />
                  Customer Information
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-primary"
                  onClick={() => window.location.href = `/customer/${customerDetails.publicId}`}
                >
                  <ExternalLink size={12} className="mr-1" />
                  View Customer
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="font-medium">
                    {customerDetails.firstName} {customerDetails.lastName}
                    {customerDetails.companyName && (
                      <span className="block text-sm text-muted-foreground mt-1">
                        {customerDetails.companyName}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">{customerDetails.street}</p>
                  <p className="text-sm text-muted-foreground">
                    {customerDetails.city}, {customerDetails.state} {customerDetails.zipCode}
                  </p>
                  <p className="text-sm text-muted-foreground">{customerDetails.country}</p>
                  <Separator className="my-2" />
                  <div className="text-sm">
                    <p className="text-muted-foreground">{customerDetails.emailAddress}</p>
                    <p className="text-muted-foreground">{customerDetails.phoneNumber}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {signature && (
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Signature
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md p-4 bg-muted/20">
                  <img
                    src={signature}
                    alt="Signature"
                    className="max-h-[100px]"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {userDetails && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground"
                onClick={() => window.location.href = `/user/${userDetails.publicId}`}
              >
                <User size={12} className="mr-1" />
                Created by {userDetails.firstName} {userDetails.lastName}
              </Button>
            </div>
          )}

          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <CreditCard size={14} />
                  Record in Accounting
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!transactionRecorded ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Select Account
                        </label>
                        <Select 
                          value={selectedAccount} 
                          onValueChange={setSelectedAccount}
                          disabled={isRecordingTransaction}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select an account" />
                          </SelectTrigger>
                          <SelectContent>
                            {chartOfAccounts.length === 0 ? (
                              <SelectItem value="na" disabled>No suitable accounts found</SelectItem>
                            ) : (
                              <>
                                <SelectItem value="na" disabled>Choose an account</SelectItem>
                                {chartOfAccounts.map((account: any) => (
                                  <SelectItem 
                                    key={account.id} 
                                    value={account.id.toString()}
                                    className="flex items-center"
                                  >
                                    <span className="font-mono text-xs text-muted-foreground mr-2">
                                      {account.code}
                                    </span>
                                    <span>{account.name}</span>
                                    <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-700">
                                      {account.type}
                                    </span>
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Select an income or asset account to record this payment
                        </p>
                      </div>
                      
                      <div className="flex items-end">
                        <Button
                          onClick={handleRecordTransaction}
                          disabled={!selectedAccount || isRecordingTransaction}
                          className="w-full md:w-auto"
                        >
                          {isRecordingTransaction ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          Record Transaction
                        </Button>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-blue-800 text-sm">
                      <p className="font-medium mb-1">Accounting Information</p>
                      <p>Recording this payment will create a credit transaction in the selected account.</p>
                    </div>
                  </>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded p-4 text-green-800 flex items-center">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">Transaction Successfully Recorded</p>
                      <p className="text-sm">This payment has been recorded in your accounting system.</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="hidden">
            <PrintableDocument
              ref={printableRef}
              document={{
                id: receiptId,
                invoiceNumber: invoiceNumber,
                number: receiptNumber,
                type: 'receipt',
                date: new Date().toISOString().split('T')[0],
                currency,
                amount,
                invoiceAmount,
                companyDetails,
                customerDetails: {
                  companyName: customerDetails.companyName,
                  name: `${customerDetails.firstName} ${customerDetails.lastName}`,
                  address: `${customerDetails.street}, ${customerDetails.city}, ${customerDetails.state}, ${customerDetails.country}`,
                  emailAddress: customerDetails.emailAddress,
                  phoneNumber: customerDetails.phoneNumber,
                },
                paymentMethodName,
                paymentDate,
                paymentReference,
                paymentAccount,
                logo,
                signature,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}